import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import { PostCard, Row } from "../HelperFunctions/HelperFunctions";
import {
  RATE_FIELDS,
  formatRateValue,
  normaliseRates,
} from "./rateFields";
import {
  calculateTotalMonthlyFees,
  loadPayingForRows,
  subscribeAdviserRates,
} from "./ratesService";
import { getSavedGroupDetails } from "../../../GroupDetails/groupDetailsService";
import { subscribeIODetails } from "../IODetails/ioDetailsService";
import { toMonthlyFeeDateValue } from "../../../Accounts/MonthlyPayments/utils/monthlyFeeGuideline";
import RatesEditLogs from "./RatesEditLogs";

const formatMonthOnly = (value) => {
  const [year, month] = toMonthlyFeeDateValue(value).split("-");
  if (!year || !month) return "-";

  return new Date(`${year}-${month}-01T12:00:00`).toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
  });
};

const getGroupRecipientId = (value = "") => {
  const text = String(value || "").trim();
  return text.startsWith("group:") ? text.slice("group:".length) : "";
};

const toValueList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);

const getGroupRecipientIds = (value = []) =>
  toValueList(value).map(getGroupRecipientId).filter(Boolean);

const getGroupRecipientLabel = (value = "", groupRecords = {}) => {
  const recipients = toValueList(value);
  if (!recipients.length) return "-";

  return recipients
    .map((recipient) => {
      const groupId = getGroupRecipientId(recipient);
      if (!groupId) return recipient;

      const groupRecord = groupRecords[groupId] || {};
      const groupName =
        groupRecord.groupName ||
        groupRecord.group?.GroupName ||
        groupRecord.group?.groupName ||
        "";

      return groupName ? `${groupName} | Group ${groupId}` : `Group ${groupId}`;
    })
    .join(", ");
};

const toIdList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);

export default function RatesTab({
  adviserId,
  adviserEmail,
  details,
  canEdit,
}) {
  const navigate = useNavigate();
  const [rates, setRates] = useState(normaliseRates());
  const [payingFor, setPayingFor] = useState([]);
  const [message, setMessage] = useState("");
  const [groupRecipientsById, setGroupRecipientsById] = useState({});
  const [groupBankDetails, setGroupBankDetails] = useState({});

  useEffect(() => {
    setMessage("");
    return subscribeAdviserRates(
      adviserId,
      adviserEmail,
      setRates,
      () => setMessage("Rates could not be loaded."),
    );
  }, [adviserEmail, adviserId]);

  useEffect(() => {
    let active = true;

    loadPayingForRows(adviserEmail, adviserId)
      .then((rows) => {
        if (active) setPayingFor(rows);
      })
      .catch(() => {
        if (active) setMessage("Paying-for details could not be loaded.");
      });

    return () => {
      active = false;
    };
  }, [adviserEmail, adviserId]);

  const totalMonthlyFees = useMemo(
    () =>
      calculateTotalMonthlyFees({
        monthlyFees: rates.monthlyFees,
        paidBy: rates.paidBy,
        adviserEmail,
        payingFor,
      }),
    [adviserEmail, payingFor, rates.monthlyFees, rates.paidBy],
  );

  useEffect(() => {
    let active = true;

    const unsubscribe = subscribeIODetails(
      adviserEmail,
      (ioDetails) => {
        const groupId = toIdList(ioDetails?.groupId)[0] || "";

        if (!groupId) {
          if (active) setGroupBankDetails({});
          return;
        }

        getSavedGroupDetails(groupId)
          .then((groupRecord) => {
            if (active) setGroupBankDetails(groupRecord?.bankDetails || {});
          })
          .catch((error) => {
            console.error("Failed to load group bank details:", error);
            if (active) setGroupBankDetails({});
          });
      },
      () => {
        if (active) setGroupBankDetails({});
      },
    );

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [adviserEmail]);

  useEffect(() => {
    let active = true;
    const groupIds = [
      ...getGroupRecipientIds(rates.sendCommissionPaymentsTo),
      ...getGroupRecipientIds(rates.emailCommissionStatementsTo),
    ];
    const uniqueGroupIds = [...new Set(groupIds)];

    if (!uniqueGroupIds.length) {
      setGroupRecipientsById({});
      return undefined;
    }

    Promise.all(
      uniqueGroupIds.map(async (groupId) => [
        groupId,
        await getSavedGroupDetails(groupId),
      ]),
    )
      .then((entries) => {
        if (active) setGroupRecipientsById(Object.fromEntries(entries));
      })
      .catch((error) => {
        console.error("Failed to load group recipients:", error);
        if (active) setGroupRecipientsById({});
      });

    return () => {
      active = false;
    };
  }, [rates.emailCommissionStatementsTo, rates.sendCommissionPaymentsTo]);

  const handleEdit = () => {
    if (!adviserId) return;

    navigate(`/advisers/${encodeURIComponent(adviserId)}/rates/edit`, {
      state: {
        adviserKey: adviserId,
        adviserEmail,
        details,
        returnTo: `/advisers/${encodeURIComponent(adviserId)}?tab=rates`,
      },
    });
  };

  return (
    <div className="smFeed">
      {canEdit ? (
        <div className="smEditActions">
          <div>
            <div className="FP_Body_Semibold">Rates and Bank Details</div>
            <p className="Content-Small muted">
              Adviser commission rates, monthly fees and bank details.
            </p>
          </div>
          <SecondaryButton label="Edit details" onClick={handleEdit} />
        </div>
      ) : null}

      {message ? (
        <div className="Content-Small adviserLicenseError">{message}</div>
      ) : null}

      <PostCard title="Rates">
        <div className="smGrid2">
          {RATE_FIELDS.map((field) => (
            <Row
              key={field.key}
              label={
                field.key === "monthlyFees"
                  ? "Monthly Fees"
                  : field.label
              }
              value={
                rates[field.key] === ""
                  ? "-"
                  : field.key === "monthlyFees"
                    ? `£${Number(rates[field.key]).toFixed(2)}`
                    : formatRateValue(rates[field.key], field.type)
              }
            />
          ))}
          <Row
            label="Monthly Fee Permission"
            value={rates.monthlyFeePermission || "-"}
          />
          <Row
            label="Monthly Fees From Date"
            value={formatMonthOnly(
              rates.monthlyFeeFromDate || details?.adviserDoc?.dateAppointed,
            )}
          />
          <Row
            label="Monthly Fees To Date"
            value={formatMonthOnly(
              rates.monthlyFeeToDate || details?.adviserDoc?.dateLeft,
            )}
          />
          <Row label="Paid By" value={rates.paidBy || "-"} />
          <Row
            label="Total Monthly Fees Charged"
            value={
              totalMonthlyFees === ""
                ? "-"
                : `£${Number(totalMonthlyFees).toFixed(2)}`
            }
          />
          <Row label="Notes" value={rates.notes || "-"} />
        </div>
        {payingFor.length ? (
          <table>
            <thead>
              <tr>
                <th className="FP_Help_Text_Semibold">Paying For</th>
                <th className="FP_Help_Text_Semibold">Monthly Fees (£)</th>
              </tr>
            </thead>
            <tbody>
              {payingFor.map((person, index) => (
                <tr key={person?.adviserId || person?.email || index}>
                  <td className="FP_Help_Text text-black">
                    {[person?.name, person?.email]
                      .filter(Boolean)
                      .join(" | ") || "-"}
                  </td>
                  <td className="FP_Help_Text text-black">
                    £
                    {Number(
                      person?.monthlyFee ?? rates.monthlyFees ?? 0,
                    ).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </PostCard>

      <PostCard title="Bank Details">
        <div className="smBankDetailsForm">
          <div className="smBankDetailsSection">
            <div className="FP_Body_Semibold">Adviser Bank Details</div>
            <Row label="Sort Code" value={rates.adviserSortCode || "-"} />
            <Row
              label="Account Number"
              value={rates.adviserAccountNumber || "-"}
            />
            <Row
              label="Account Holder"
              value={rates.adviserAccountHolder || "-"}
            />
          </div>

          <div className="smBankDetailsSection">
            <div className="smSectionHeaderInline">
              <div className="FP_Body_Semibold">Group Bank Details</div>
              {/* {canEdit ? (
                <ButtonTextLink
                  label="Edit"
                  onClick={handleEditGroupBankDetails}
                />
              ) : null} */}
            </div>
            <Row
              label="Sort Code"
              value={groupBankDetails.arSortCode || rates.arSortCode || "-"}
            />
            <Row
              label="Account Number"
              value={
                groupBankDetails.arAccountNumber ||
                rates.arAccountNumber ||
                "-"
              }
            />
            <Row
              label="Account Holder"
              value={
                groupBankDetails.arAccountHolder ||
                rates.arAccountHolder ||
                "-"
              }
            />
            <Row
              label="Statement Details To"
              value={groupBankDetails.emailCommissionStatementsTo || "-"}
            />
            <Row
              label="Statement Details CC"
              value={groupBankDetails.emailCommissionStatementsCc || "-"}
            />
          </div>

          <div className="smBankDetailsSection">
            <div className="FP_Body_Semibold">
              Commission Payment Details
            </div>
            <Row
              label="Adviser to Send Commission Payments"
              value={getGroupRecipientLabel(
                rates.sendCommissionPaymentsTo,
                groupRecipientsById,
              )}
            />
            <Row
              label="Send Details"
              value={getGroupRecipientLabel(
                rates.emailCommissionStatementsTo,
                groupRecipientsById,
              )}
            />
            <Row
              label="CC"
              value={rates.emailCommissionStatementsCc || "-"}
            />
          </div>
        </div>
      </PostCard>

      <RatesEditLogs adviserId={adviserId} activeTab="rates" />
    </div>
  );
}
